/* eslint-disable @typescript-eslint/ban-types */

import { FullPageLoader } from '../components/FullPageLoader';
import { IntakeCoachImageFreeTextScreen } from '../containers/IntakeCoachImageFreeTextScreen';
import { IntakeFreeTextScreen } from '../containers/IntakeFreeTextScreen';
import { IntakeIntroScreen } from '../containers/IntakeIntroScreen';
import { IntakeMultiSelectScreen } from '../containers/IntakeMultiSelectScreen';
import { IntakeMultiTextScreen } from '../containers/IntakeMultiTextScreen';
import { IntakeNumericalResponseScreen } from '../containers/IntakeNumericalResponseScreen';
import { IntakeReviewScreen } from '../containers/IntakeReviewScreen';
import { IntakeFormQuestionData } from '../selectors/intakeFormSelectors';

export enum Screens {
    Loading,
    Screen1,
    Screen2,
    Screen3,
    Screen4,
    Screen5,
    Screen6,
    Screen7,
    Screen8,
    Review,
    // Apparently TS (or at least our version) doesn't have a standard way to derive the number of
    // elements in an enum (https://stackoverflow.com/questions/38034673/determine-the-number-of-enum-elements-typescript)
    __LENGTH,
}

export const questionNamesAsCamel = {
    MOTIVATION_TO_SEEK_COACHING: 'motivationToSeekCoaching',
    OUTCOMES_TO_ACHIEVE_ONE: 'outcomesToAchieveOne',
    OUTCOMES_TO_ACHIEVE_TWO: 'outcomesToAchieveTwo',
    OUTCOMES_TO_ACHIEVE_THREE: 'outcomesToAchieveThree',
    CURRENT_PERSONAL_AND_PROFESSIONAL_LIFE: 'currentPersonalAndProfessionalLife',
    POST_SESSIONS_PERSONAL_AND_PROFESSIONAL_LIFE: 'postSessionsPersonalAndProfessionalLife',
    RANK_SUPPORT_ACCOUNTABILITY: 'rankSupportAccountability',
    RANK_SUPPORT_BRAINSTORMING: 'rankSupportBrainStorming',
    RANK_SUPPORT_EXPLORING_OBSTACLES: 'rankSupportExploringObstacles',
    RANK_SUPPORT_INSIGHT_INTO_POTENTIAL: 'rankSupportInsightIntoPotential',
    RANK_SUPPORT_VISION: 'rankSupportVision',
    RANK_SUPPORT_ENCOURAGEMENT: 'rankSupportEncouragement',
    EXPERIENCED_COACHING_BEFORE: 'experiencedCoachingBefore',
    ANYTHING_ELSE: 'anythingElse',
};

export const questionNames = {
    motivationToSeekCoaching: 'MOTIVATION_TO_SEEK_COACHING',
    outcomesToAchieveOne: 'OUTCOMES_TO_ACHIEVE_ONE',
    outcomesToAchieveTwo: 'OUTCOMES_TO_ACHIEVE_TWO',
    outcomesToAchieveThree: 'OUTCOMES_TO_ACHIEVE_THREE',
    currentPersonalAndProfessionalLife: 'CURRENT_PERSONAL_AND_PROFESSIONAL_LIFE',
    postSessionsPersonalAndProfessionalLife: 'POST_SESSIONS_PERSONAL_AND_PROFESSIONAL_LIFE',
    rankSupportAccountability: 'RANK_SUPPORT_ACCOUNTABILITY',
    rankSupportBrainStorming: 'RANK_SUPPORT_BRAINSTORMING',
    rankSupportExploringObstacles: 'RANK_SUPPORT_EXPLORING_OBSTACLES',
    rankSupportInsightIntoPotential: 'RANK_SUPPORT_INSIGHT_INTO_POTENTIAL',
    rankSupportVision: 'RANK_SUPPORT_VISION',
    rankSupportEncouragement: 'RANK_SUPPORT_ENCOURAGEMENT',
    experiencedCoachingBefore: 'EXPERIENCED_COACHING_BEFORE',
    anythingElse: 'ANYTHING_ELSE',
};

export const screenComponentTypes = Object.freeze({
    [Screens.Loading]: FullPageLoader,
    [Screens.Screen1]: IntakeIntroScreen,
    [Screens.Screen2]: IntakeFreeTextScreen,
    [Screens.Screen3]: IntakeMultiSelectScreen,
    [Screens.Screen4]: IntakeFreeTextScreen,
    [Screens.Screen5]: IntakeNumericalResponseScreen,
    [Screens.Screen6]: IntakeMultiTextScreen,
    [Screens.Screen7]: IntakeFreeTextScreen,
    [Screens.Screen8]: IntakeCoachImageFreeTextScreen, // TODO: make changes to be able to use IntakeFreeTextScreen component for this step
    [Screens.Review]: IntakeReviewScreen,
});

export const questionDataMap = Object.freeze({
    2: {
        questionId: questionNames.motivationToSeekCoaching,
        questionText: 'intakeForms:motivation.question_text',
        questionPlaceholder: 'intakeForms:motivation.placeholder',
        screenIndex: 1,
    },
    3: {
        answerIds: ['1', '2', '3'],
        questionId: questionNames.experiencedCoachingBefore,
        questionText: 'intakeForms:experience.question_text',
        screenIndex: 2,
    },
    4: {
        questionId: questionNames.currentPersonalAndProfessionalLife,
        questionText: 'intakeForms:current_personal_professional.question_text',
        questionPlaceholder: 'intakeForms:current_personal_professional.placeholder',
        screenIndex: 3,
    },
    5: {
        questionIds: [
            {
                subQuestionId: questionNames.rankSupportAccountability,
                subQuestionTitle: 'intakeForms:rank_support.accountability.subtitle',
                subQuestionSubtitle: 'intakeForms:rank_support.accountability.subtext',
            },
            {
                subQuestionId: questionNames.rankSupportBrainStorming,
                subQuestionTitle: 'intakeForms:rank_support.brainstorming.subtitle',
                subQuestionSubtitle: 'intakeForms:rank_support.brainstorming.subtext',
            },
            {
                subQuestionId: questionNames.rankSupportInsightIntoPotential,
                subQuestionTitle: 'intakeForms:rank_support.insight.subtitle',
                subQuestionSubtitle: 'intakeForms:rank_support.insight.subtext',
            },
            {
                subQuestionId: questionNames.rankSupportExploringObstacles,
                subQuestionTitle: 'intakeForms:rank_support.exploring.subtitle',
                subQuestionSubtitle: 'intakeForms:rank_support.exploring.subtext',
            },
            {
                subQuestionId: questionNames.rankSupportVision,
                subQuestionTitle: 'intakeForms:rank_support.vision.subtitle',
                subQuestionSubtitle: 'intakeForms:rank_support.vision.subtext',
            },
            {
                subQuestionId: questionNames.rankSupportEncouragement,
                subQuestionTitle: 'intakeForms:rank_support.encouragement.subtitle',
                subQuestionSubtitle: 'intakeForms:rank_support.encouragement.subtext',
            },
        ],
        questionText: 'intakeForms:rank_support.question_text',
        screenIndex: 4,
    },
    6: {
        questionIds: [
            { subQuestionId: questionNames.outcomesToAchieveOne },
            { subQuestionId: questionNames.outcomesToAchieveTwo },
            { subQuestionId: questionNames.outcomesToAchieveThree },
        ],
        questionText: 'intakeForms:outcomes_to_achieve.question_text',
        questionPlaceholder: 'intakeForms:outcomes_to_achieve.placeholder',
        screenIndex: 5,
    },
    7: {
        questionId: questionNames.postSessionsPersonalAndProfessionalLife,
        questionText: 'intakeForms:post_session_personal_professional.question_text',
        questionPlaceholder: 'intakeForms:post_session_personal_professional.placeholder',
        screenIndex: 6,
    },
    8: {
        questionId: questionNames.anythingElse,
        questionText: 'intakeForms:anything_else.question_text',
        questionPlaceholder: 'intakeForms:anything_else.placeholder',
        screenIndex: 7,
    },
});

export const questionsToScreenMap = Object.freeze(
    Object.entries(questionDataMap).reduce((accum, curr) => {
        const [screen, data] = curr;
        const { questionId, questionIds } = data as IntakeFormQuestionData;

        if (questionId) {
            return { ...accum, [questionId]: screen };
        }

        const subIdsMap = {};

        (questionIds || []).forEach(({ subQuestionId }) => {
            subIdsMap[subQuestionId] = screen;
        });

        return { ...accum, ...subIdsMap };
    }, {}),
);

export const answersMap = Object.freeze({
    1: {
        value: 'YES_EXPERIENCED_COACHING',
        text: 'intakeForms:experience.yes_experienced_coaching',
    },
    2: {
        value: 'YES_EXPERIENCED_THERAPY',
        text: 'intakeForms:experience.yes_experienced_therapy',
    },
    3: { value: 'NO_EXPERIENCE', text: 'intakeForms:experience.no_no_experience' },
});

export interface IntakeFormProps {
    saveResponse: (questionId: string, response: string | string[]) => Promise<void>;
    saveMultipleResponses: (questionMap: object, submitToCoach?: boolean) => Promise<void>;
    skipQuestion: () => void;
    goBack: () => void;
    onAbandonClick: (abandonedResponse: string | object) => void;
    toggleCoachMoreInfo: () => void;
    coachAvatar: string;
    coachFirstName: string;
    coachLastName: string;
    coachCredentials: string;
    userFirstName: string;
    currentScreen: Screens;
    errorMessage: string;
    responses: string | string[] | object | null;
    screenCount: number;
    allResponses: object;
}

export interface AnswerOptionData {
    value: string;
    text: string;
}

export enum IntakeFormTestID {
    Page = 'IntakeFormTestId.Page',
    IntroScreen = 'IntakeFormTestId.IntroScreen',
    CoachInfoButton = 'IntakeFormTestId.CoachInfoButton',
    CoachMoreInfoModal = 'IntakeFormTestId.CoachMoreInfoModal',
    StepButtons = 'IntakeFormTestId.StepButtons',
    SkipButton = 'IntakeFormTestId.SkipButton',
    ForwardButton = 'IntakeFormTestId.ForwardButton',
    AbandonModal = 'IntakeFormTestID.AbandonModal',
}
